import {
    BatchPaymentResponse,
    Borrower,
    CompleteCardPaymentResponse,
    CompleteEnroll3DsResponseData,
    ConsumptionData,
    ConsumptionDataSet,
    CreditAllocationInfo,
    Enroll3DResponseData,
    EstimatedDailyConsumptionResponse,
    EstimatedPostpaidDailyConsumption,
    EstimatedRunOutDate,
    FraudBill,
    GeneralMeterInvoicePayload,
    GetPostPaidBillParams,
    GetPostPaidBillResponse,
    InvoiceItems,
    OutstandingCreditInfo,
    PaymentHistoryStatementData,
    PaySmallSmallBreakdown,
    PaySmallSmallCheckoutConfig,
    PaySmallSmallCreditConfig,
    PaySmallSmallCreditLimitPercentage,
    PaySmallSmallCreditScore,
    PaySmallSmallHistoryRequestQuery,
    PaySmallSmallManualRepaymentStatusCheck,
    PaySmallSmallPayment,
    PostPaidConsumptionData,
    RecentThirdPartyTransactionsResponse,
    ServiceRequestPaymentPayload,
    SingleInvoiceResponse,
    ThirdPartyFilteredTransactions,
    ThirdPartyTransactionStatusResponse,
    TopUpMeterPayload,
    TopUpMeterResponse,
    TotalTransactionsResponse,
    Transaction,
    TransactionFilters,
    TransactionResponse,
    TransferMeterCreditRequest,
    TransferMeterCreditResponse,
    UserProfileStatus,
} from '@/types/transactions'
import { ApiListResponse, ApiResponse } from '../types'
import { AxiosClient } from '../utils/clients'
import { PaySmallSmallCheckoutType } from '@/constants/transactions'

export default class TransactionRepository {
    private BASE_URL = process.env.NEXT_PUBLIC_FINANCES_BASE_URL
    // private BASE_URL = 'https://z657dj7j-7032.uks1.devtunnels.ms'
    private BASE_VENDOR_URL = process.env.NEXT_PUBLIC_VENDORS_BASE_URL
    constructor(private client: AxiosClient) {}

    async getTotalTransactionsSpend(filter: TransactionFilters) {
        const res = await this.client.get<TotalTransactionsResponse>(`${this.BASE_URL}/transactions/total-spend`, {
            params: filter,
        })
        return res.data
    }

    async getTransactions(filter: TransactionFilters) {
        const res = await this.client.get<TransactionResponse>(`${this.BASE_URL}/transactions`, {
            params: filter,
        })
        return res.data
    }

    async topUpMeter(meterId: string, payload: TopUpMeterPayload) {
        const res = await this.client.post<TopUpMeterResponse>(
            `${this.BASE_URL}/transactions/recharge-meter/${meterId}`,
            payload,
        )
        return res.data
    }

    async topUpMeterWithPaySmallSmall(meterId: string, payload: TopUpMeterPayload) {
        const res = await this.client.post<TopUpMeterResponse>(
            `${this.BASE_URL}/transactions/paysmallsmall/advance-tiering/recharge-meter/${meterId}`,
            payload,
        )
        return res.data
    }

    async topUpMeterWithCard(meterId: string, payload: TopUpMeterPayload) {
        const res = await this.client.post<TopUpMeterResponse>(
            `${this.BASE_URL}/payment/recharge-meter/${meterId}`,
            payload,
        )
        return res.data
    }

    async completeCardPayment(meterId: string, transactionId: string) {
        const res = await this.client.patch<ApiResponse<CompleteCardPaymentResponse>>(
            `${this.BASE_URL}/payment/recharge-meter/${meterId}/complete-card-payment/${transactionId}`,
        )
        return res.data
    }

    async checkTransactionStatus(transactionId: string, isServiceRequest: boolean = false) {
        const res = await this.client.get<ApiResponse<Transaction>>(
            `${this.BASE_URL}/transactions/${transactionId}/status?${
                isServiceRequest ? 'isServiceRequest=' + isServiceRequest : ''
            }`,
        )
        return res.data
    }

    async checkBulkTransactionStatus(batchRechargeId: string, size: number) {
        const res = await this.client.get<ApiResponse<BatchPaymentResponse>>(
            `${this.BASE_URL}/transactions/bulk/transaction/${batchRechargeId}/status/${size}`,
        )
        return res.data
    }

    async getRecentThirdPartyTransactions() {
        const res = await this.client.get<RecentThirdPartyTransactionsResponse>(
            `${this.BASE_VENDOR_URL}/transaction/recent-transactions`,
        )
        return res.data
    }

    async getThirdPartyTransactionStatus(transactionId: string) {
        const res = await this.client.get<ApiResponse<ThirdPartyTransactionStatusResponse>>(
            `${this.BASE_URL}/transaction/${transactionId}/status`,
        )
        return res.data
    }

    async getThirdPartyTransaction(filter: TransactionFilters) {
        const res = await this.client.get<ApiResponse<ThirdPartyFilteredTransactions>>(
            `${this.BASE_VENDOR_URL}/transaction`,
            {
                params: filter,
            },
        )
        return res.data
    }

    async getThirdPartyTotalSpent(filter: TransactionFilters) {
        const res = await this.client.get<
            ApiResponse<{
                sum: number
                name: string
                count: number
            }>
        >(`${this.BASE_VENDOR_URL}/transaction/total-spend`, {
            params: filter,
        })
        return res.data
    }

    async topUpThirdPartyQuota(invoiceId: string, payload: { amount: string | number; walletId: string }) {
        const res = await this.client.post<{
            data: ApiResponse<{}>
        }>(`${this.BASE_VENDOR_URL}/vendors/pay/${invoiceId}`, payload)
        return res.data
    }

    async topUpThirdPartyMeter(vendorId: string, payload: { amount: string | number; walletId: string }) {
        const res = await this.client.post<TopUpMeterResponse>(
            `${this.BASE_VENDOR_URL}/vendors/${vendorId}/invoices/pay/`,
            payload,
        )
        return res.data
    }

    async checkThirdPartyTransactionStatus(transactionId: string) {
        const res = await this.client.get<ApiResponse<Transaction>>(
            `${this.BASE_VENDOR_URL}/transaction/${transactionId}/status`,
        )
        return res.data
    }

    async createThirdPartyInvoice(vendorId: string, payload: InvoiceItems) {
        const formatedPayload = {
            invoiceItems: [payload],
        }
        const res = await this.client.post<ApiResponse<Transaction>>(
            `${this.BASE_VENDOR_URL}/vendors/${vendorId}/invoices/`,
            formatedPayload,
        )
        return res.data
    }
    async createGeneralMeterInvoice(meterId: string, payload: GeneralMeterInvoicePayload) {
        const res = await this.client.post<ApiResponse<any>>(
            `${this.BASE_URL}/transactions/recharge-meter/${meterId}`,
            payload,
        )
        return res.data
    }

    async createInvoiceUSSD(id: string, payload: InvoiceItems) {
        const res = await this.client.post<{
            data: ApiResponse<Transaction>
        }>(`${this.BASE_VENDOR_URL}/vendors/${id}/invoices`, payload)
        return res.data
    }

    async createInvoiceMomo(
        id: string,
        payload: {
            amount: string | number
            walletId: string
        },
    ) {
        const res = await this.client.post<{
            data: ApiResponse<Transaction>
        }>(`${this.BASE_VENDOR_URL}/vendors/${id}/invoices/pay`, payload)
        return res.data
    }
    async getSingleInvoice(id: string) {
        const res = await this.client.get<SingleInvoiceResponse>(`${this.BASE_URL}/invoices/${id}`)
        return res.data
    }

    //get postpaid bill
    async getPostpaidBillUrl(params: GetPostPaidBillParams) {
        const res = await this.client.get<GetPostPaidBillResponse>(
            `${this.BASE_URL}/customers/get-customer-postpaid-bill`,
            { params },
        )
        return res.data
    }

    // get consumption data summary
    async getConsumptionData(params: { accountNumber: string; period: string }) {
        const res = await this.client.get<ApiResponse<ConsumptionData>>(
            `https://api-ubs.gov-gh.com/ubsdata/get-consumption-data/summary`,
            { params },
        )
        return res.data
    }

    async getDailyConsumption(params: { accountNumber: string }) {
        const res = await this.client.get<ApiResponse<EstimatedDailyConsumptionResponse>>(
            `https://api-ubs.gov-gh.com/ubsconsumptions/get-daily-consumption/${params.accountNumber}`,
        )
        return res.data
    }

    async getPostpaidDailyConsumption(params: { accountNumber: string; period?: string }) {
        // /ubsconsumptions/get-postpaid-consumption/{accountNumber}
        const res = await this.client.get<ApiResponse<EstimatedPostpaidDailyConsumption>>(
            `https://api-ubs.gov-gh.com/ubsconsumptions/get-postpaid-daily-consumption/${params.accountNumber}`,
        )
        return res.data
    }

    async getFlatRateDailyConsumption(params: { accountNumber: string }) {
        // /ubsconsumptions/get-postpaid-consumption/{accountNumber}
        const res = await this.client.get<ApiResponse<EstimatedPostpaidDailyConsumption>>(
            `https://api-ubs.gov-gh.com/ubsconsumptions/get-flat-rate-daily-consumption/${params.accountNumber}`,
        )
        return res.data
    }

    async getConsumptionForMonths(params: { accountNumber: string; numberOfMonths: number }) {
        const res = await this.client.get<ApiResponse<ConsumptionDataSet>>(
            ` https://api-ubs.gov-gh.com/ubsconsumptions/get-consumption-from-months/${params.accountNumber}/${params.numberOfMonths}`,
            { params },
        )
        return res.data
    }

    async getConsumptionFor(params: { accountNumber: string }) {
        const res = await this.client.get<
            ApiResponse<{
                consumptionDataSet: ConsumptionDataSet
            }>
        >(`https://api-ubs.gov-gh.com/ubsconsumptions/get-consumption-for/${params.accountNumber}`, { params })
        return res.data
    }

    async getConsumptionRunOutDateFor(params: { accountNumber: string }) {
        const res = await this.client.get<ApiResponse<EstimatedRunOutDate>>(
            `https://api-ubs.gov-gh.com/ubsconsumptions/get-consumption-run-outdate/${params.accountNumber}`,
            { params },
        )
        return res.data
    }

    async getPostpaidConsumptionData(params: { accountNumber: string; period: string }) {
        const url = `https://api-ubs.gov-gh.com/ubsconsumptions/v1/get-postpaid-estimate-consumption/${params.accountNumber}`
        const res = await this.client.get<ApiResponse<PostPaidConsumptionData[]>>(url, { params })
        return res.data
    }

    async getPostpaidPaymentHistory(params: TransactionFilters) {
        const res = await this.client.get<ApiResponse<PaymentHistoryStatementData>>(
            `https://api-ubs.gov-gh.com/ubsconsumptions/statement-list/${params.AccountNumber}/${params.month}?meterCategory=${params.MeterCategory}`,
            {
                params,
            },
        )

        return res.data
    }

    async getFlatRateConsumptionData(params: { accountNumber: string }) {
        const url = `https://api-ubs.gov-gh.com/ubsconsumptions/get-flat-rate-consumption/${params.accountNumber}`
        const res = await this.client.get<ApiResponse<PostPaidConsumptionData[]>>(url, { params })
        return res.data
    }

    async getFraudBills(accountNumber: string) {
        const res = await this.client.get<ApiResponse<FraudBill>>(
            `https://api-ecg-devices.gov-gh.com/devices/fraud-bills/${accountNumber}`,
        )

        return res.data
    }

    async payFraudBill({ meterId, payload, billId }: { billId: string; meterId: string; payload: TopUpMeterPayload }) {
        const res = await this.client.post<TopUpMeterResponse>(
            `${this.BASE_URL}/transactions/pay-fraud-bill/${meterId}/${billId}`,
            payload,
        )
        return res.data
    }

    async enroll3dsCard(meterId: string, referenceId: string) {
        const res = await this.client.patch<ApiResponse<Enroll3DResponseData>>(
            `${this.BASE_URL}/transactions/recharge-meter/${meterId}/card-enroll-3ds`,
            { ReferenceId: referenceId },
        )
        return res.data
    }

    async completeEnroll3dsCard(meterId: string, referenceId: string) {
        const res = await this.client.patch<ApiResponse<CompleteEnroll3DsResponseData>>(
            `${this.BASE_URL}/transactions/recharge-meter/${meterId}/complete-card-payment`,
            { ReferenceId: referenceId },
        )
        return res.data
    }

    async payServiceRequestFee(
        invoiceId: string,
        payload: {
            amount: string | number
            sourceWalletId: string
            mandateId?: string
            cardNumber?: string
            cvv?: string
            expiryMonth?: string
            expiryYear?: string
        },
    ) {
        const res = await this.client.post<TopUpMeterResponse>(
            `${this.BASE_URL}/api/nonenergy/${invoiceId}/recharge`,
            payload,
        )
        return res.data
    }

    async nonEnergyEnroll3dsCard(invoiceId: string, referenceId: string) {
        const res = await this.client.patch<ApiResponse<Enroll3DResponseData>>(
            `${this.BASE_URL}/api/nonenergy/${invoiceId}/card-enroll-3ds`,
            { referenceId: referenceId },
        )
        return res.data
    }

    async nonEnergyCompleteCardPayment(invoiceId: string, referenceId: string) {
        const res = await this.client.patch<ApiResponse<CompleteCardPaymentResponse>>(
            `${this.BASE_URL}/api/nonenergy/${invoiceId}/complete-card-payment/${referenceId}`,
        )
        return res.data
    }

    async getPaySmallSmallTransactionBreakdown(transactionId?: string) {
        const res = await this.client.get<ApiResponse<PaySmallSmallBreakdown>>(
            `${this.BASE_URL}/paysmallsmall/breakdown?transactionId=${transactionId ?? ''}`,
        )
        return res.data
    }

    async getPaySmallSmallHistoryList({
        pageSize = 10,
        page = 1,
        repaymentStatus = '',
    }: Partial<PaySmallSmallHistoryRequestQuery>) {
        const res = await this.client.get<ApiResponse<ApiListResponse<PaySmallSmallBreakdown[]>>>(
            `${this.BASE_URL}/paysmallsmall/history?pageSize=${pageSize}&repaymentStatus=${repaymentStatus}&page=${page}`,
        )
        return res.data
    }

    async makePaySmallSmallManualRepayment(payload: {
        amount: string | number
        transactionId: string
        callbackUrl: string
        debitMobileNumber: string
        channel: string
    }) {
        const res = await this.client.post<ApiResponse<{ transactionId: string }>>(
            `${this.BASE_URL}/paysmallsmall/manual-repayment`,
            payload,
        )
        return res.data
    }

    async checkPaySmallSmallManualRepaymentStatus(transactionId: string) {
        const res = await this.client.get<ApiResponse<PaySmallSmallManualRepaymentStatusCheck>>(
            `${this.BASE_URL}/paysmallsmall/manual-repayment-status-check/${transactionId}`,
        )
        return res.data
    }

    async paySmallSmallUserHasOverdueDebt() {
        const res = await this.client.get<ApiResponse<boolean>>(`${this.BASE_URL}/paysmallsmall/has-overdue-dept`)
        return res.data
    }

    async getPaySmallSmallStatus() {
        const res = await this.client.get<ApiResponse<UserProfileStatus>>(
            `${this.BASE_URL}/paysmallsmall/profiling/status`,
        )
        return res.data
    }

    async getPaySmallSmallCreditScore() {
        const res = await this.client.get<ApiResponse<PaySmallSmallCreditScore>>(
            `${this.BASE_URL}/paysmallsmall/credit-score`,
        )
        return res.data
    }

    async getPaySmallSmallBorrowerData() {
        const res = await this.client.get<ApiResponse<Borrower>>(`${this.BASE_URL}/paysmallsmall/borrowers`)
        return res.data
    }

    async getPaySmallSmallCreditConfig() {
        const res = await this.client.get<ApiResponse<PaySmallSmallCreditConfig>>(
            `${this.BASE_URL}/paysmallsmall/configuration/v2`,
        )
        return res.data
    }

    async getPaySmallSmallCreditLimitPercentage() {
        const res = await this.client.get<ApiResponse<PaySmallSmallCreditLimitPercentage>>(
            `${this.BASE_URL}/paysmallsmall/credit-limit-percentage/v2`,
        )
        return res.data
    }

    async getPaySmallSmallCheckoutConfig({
        checkoutType,
        installmentId,
        transactionId,
    }: {
        checkoutType: PaySmallSmallCheckoutType | string
        installmentId?: string
        transactionId?: string
    }) {
        const params: any = {
            CheckoutType: checkoutType,
        }

        if (installmentId) {
            params.InstallmentId = installmentId
        }

        if (transactionId) {
            params.TransactionId = transactionId
        }

        const res = await this.client.get<ApiResponse<PaySmallSmallCheckoutConfig>>(
            `${this.BASE_URL}/paysmallsmall/process-before-checkout`,
            {
                params,
            },
        )
        return res.data
    }

    // This function calls an endpoint to check if the ECG apps are eligible to use the Pay Small Small payment channel.
    async getPaySmallSmallPaymentChannelStatus() {
        const res = await this.client.get<ApiResponse<{ isPaymentOptionAvailable: boolean }>>(
            `${this.BASE_URL}/paysmallsmall/channels/status`,
        )
        return res.data
    }

    async getPaySmallSmallOutstandingCreditInfo() {
        const res = await this.client.get<ApiResponse<OutstandingCreditInfo>>(
            `${this.BASE_URL}/paysmallsmall/outstanding-credit-info`,
        )
        return res.data
    }

    async getOverduePayments() {
        const res = await this.client.get<ApiResponse<PaySmallSmallPayment[]>>(
            `${this.BASE_URL}/paysmallsmall/payment/overduepayment`,
        )
        return res.data
    }

    async initiateAllOutstandingPayments() {
        const res = await this.client.get<ApiResponse<{ currentDate: Date; data: PaySmallSmallPayment[] }>>(
            `${this.BASE_URL}/paysmallsmall/payment/outstanding`,
        )
        return res.data
    }
    async initiateDueNowPayment({ transactionId, installmentId }: { transactionId: string; installmentId: string }) {
        const res = await this.client.get<ApiResponse<{ currentDate: Date; data: PaySmallSmallPayment[] }>>(
            `${this.BASE_URL}/paysmallsmall/payment/${transactionId}/due-now/${installmentId}`,
        )
        return res.data
    }
    async initiateAllDuePayments(transactionId: string) {
        const res = await this.client.get<ApiResponse<{ currentDate: Date; data: PaySmallSmallPayment[] }>>(
            `${this.BASE_URL}/paysmallsmall/payment/${transactionId}`,
        )
        return res.data
    }

    async getPaySmallSmallTransaction(id: string) {
        const res = await this.client.get<ApiResponse<Transaction>>(
            `${this.BASE_URL}/paysmallsmall/meter-transaction/${id}`,
        )
        return res.data
    }

    async activatePaySmallSmallUser(payload: { profilePicture: string; ghanaCardId: string }) {
        const res = await this.client.post<ApiResponse<string>>(
            `${this.BASE_URL}/paysmallsmall/user/activation`,
            payload,
        )
        return res.data
    }

    async makeServiceRequestPayment(payload: ServiceRequestPaymentPayload) {
        const res = await this.client.post<TopUpMeterResponse>(
            `${this.BASE_URL}/transactions/service-request-payment`,
            payload,
        )
        return res.data
    }

    async completeChangeAccountDetailsCardPayment(transactionId: string) {
        const res = await this.client.patch<ApiResponse<CompleteCardPaymentResponse>>(
            `${this.BASE_URL}/transactions/service-request/complete-card-payment/${transactionId}`,
        )
        return res.data
    }

    async meterHasPreviousTransactions(meterId: string) {
        const res = await this.client.get<ApiResponse<boolean>>(
            `${this.BASE_URL}/transactions/customer-statement/check-previous-payment/${meterId}`,
        )
        return res.data
    }

    async transferMeterCredit(payload: TransferMeterCreditRequest) {
        const res = await this.client.post<ApiResponse<TransferMeterCreditResponse>>(
            `${this.BASE_URL}/credittransfer/${payload.meterId}`,
            {
                amount: payload.amount,
            },
        )
        return res.data
    }

    async getMeterCreditTransferDetails(transferId: string) {
        const res = await this.client.get<ApiResponse<Transaction>>(`${this.BASE_URL}/credittransfer/${transferId}`)
        return res.data
    }

    async getPssExistingCreditAllocationInfo() {
        const res = await this.client.get<ApiResponse<CreditAllocationInfo>>(
            `${this.BASE_URL}/paysmallsmall/existing-credit-allocation-info`,
        )
        return res.data
    }

    async getOutstandingCreditAllocationInfo() {
        const res = await this.client.get<ApiResponse<CreditAllocationInfo>>(
            `${this.BASE_URL}/paysmallsmall/credit-allocation-outstanding-info`,
        )
        return res.data
    }

    async getBorrowerVerificationStatus() {
        const res = await this.client.get<
            ApiResponse<{ hasPassedChecks: boolean; reason: string; title: string; content: string }>
        >(`${this.BASE_URL}/paysmallsmall/borrower-verification-status`)
        return res.data
    }
}
